import React from 'react';
import ReactDOMServer from 'react-dom/server';
import GeneralAgreement from '../components/GeneralAgreement';
import GeneralAgreement2024 from '../components/GeneralAgreement2024';
import NonDiscriminationStament from '../components/NonDiscriminationStament';
import ProgramAgreement from '../components/ProgramAgreement';
import ProgramAgreement2024 from '../components/ProgramAgreement2024';
import SchoolMarketAgreement from '../components/SchoolMarketAgreement';
import SchoolMarketAgreement2024 from '../components/SchoolMarketAgreement2024';
import TeachersAidProgramAgreement from '../components/TeachersAidProgramAgreement';
import TeachersAidProgramAgreement2024 from '../components/TeachersAidProgramAgreement2024';
import MOUSignatures from '../components/MOUSignatures';
import apolloClient from '../apollo/client';
import districtsQuery from '../apollo/queries/districts';

/**
 * Opens a new browser window with all MOU agreements rendered together,
 * then auto-triggers the print dialog for PDF download.
 *
 * @param {Object} data - Application data (e.g., data.applicationBackpackBuddy)
 * @param {boolean} isPrev2024 - Whether to use pre-2024 agreement versions
 * @param {string} programType - 'backpackBuddy' | 'schoolMarket' | 'teachersAid'
 */
const downloadMOU = async (data, isPrev2024, programType) => {
  // Determine which program agreement to render
  const getProgramAgreement = () => {
    switch (programType) {
      case 'schoolMarket':
        return isPrev2024 ? <SchoolMarketAgreement /> : <SchoolMarketAgreement2024 />;
      case 'teachersAid':
        return isPrev2024 ? <TeachersAidProgramAgreement /> : <TeachersAidProgramAgreement2024 />;
      case 'backpackBuddy':
      default:
        return isPrev2024 ? <ProgramAgreement /> : <ProgramAgreement2024 />;
    }
  };

  // Program title mapping for the document header
  const programTitleMap = {
    backpackBuddy: 'Backpack Buddy Program Agreement Acknowledgement',
    schoolMarket: 'School Market Program Agreement Acknowledgement',
    teachersAid: 'Teachers Aid Program Agreement Acknowledgement',
  };
  const programTitle = programTitleMap[programType] || 'Program Agreement Acknowledgement';

  const school = (data && data.school) || {};
  let districtDisplay = '';
  if (school.districtId) {
    try {
      const result = await apolloClient.query({ query: districtsQuery });
      const districts = (result && result.data && result.data.allDistricts) || [];
      const match = districts.find(d => d.districtId === school.districtId);
      districtDisplay = (match && match.name) || '';
    } catch (e) {
      districtDisplay = '';
    }
  }
  const headerHTML = `
    <div class="mou-doc-header">
      <h2 class="mou-doc-title">${programTitle}</h2>
      <div class="mou-school-info">
        <div class="mou-school-col">
          <p><strong>School Name:</strong> ${school.name || ''}</p>
          <p><strong>Address Line 1:</strong> ${school.address1 || ''}</p>
          <p><strong>City:</strong> ${school.city || ''}</p>
          <p><strong>State:</strong> ${school.state || ''}</p>
        </div>
        <div class="mou-school-col">
          <p><strong>School District:</strong> ${districtDisplay}</p>
          <p><strong>Address Line 2:</strong> ${school.address2 || ''}</p>
          <p><strong>County:</strong> ${school.county || ''}</p>
          <p><strong>Postal Code:</strong> ${school.zip || ''}</p>
        </div>
      </div>
    </div>
  `;

  // Build acknowledgement signature block (used between sections)
  const acknowledgementHTML = (sectionLabel, fieldLabel, signName) => `
    <div class="mou-acknowledgement">
      <p class="mou-ack-section-label">${sectionLabel}</p>
      <p class="mou-ack-field-label">${fieldLabel}</p>
      <p class="mou-ack-name-display">${signName || ''}</p>
      <div class="mou-ack-input-row">
        <div class="mou-ack-input">${signName || ''}</div>
        <div class="mou-ack-agree-row">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="12" fill="#64B5F6"/>
            <path d="M9 12l2 2 4-4" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="mou-ack-agree-label"><strong>I Agree</strong></span>
        </div>
      </div>
    </div>
  `;

  // Render each section separately so we can interleave signature blocks
  const generalAgreementHTML = ReactDOMServer.renderToStaticMarkup(
    isPrev2024
      ? <GeneralAgreement data={data} hideSignatures />
      : <GeneralAgreement2024 data={data} hideSignatures />
  );
  const programAgreementHTML = ReactDOMServer.renderToStaticMarkup(getProgramAgreement());
  const nonDiscriminationHTML = ReactDOMServer.renderToStaticMarkup(<NonDiscriminationStament data={data} />);
  const finalSignaturesHTML = ReactDOMServer.renderToStaticMarkup(<MOUSignatures data={data} />);

  let agreementHTML = `
    ${generalAgreementHTML}
    ${acknowledgementHTML('I acknowledge that I have read and understood the Memorandum of Understanding above.', 'Agreement Sign', data.nameSignFirst || '')}
    ${programAgreementHTML}
    ${acknowledgementHTML('I acknowledge that I have read and understood the Program Agreement above.', 'Program Sign', data.nameSignThird || '')}
    ${nonDiscriminationHTML}
    ${acknowledgementHTML('I acknowledge that I have read and understood the USDA Nondiscrimination Statement above.', 'Nondiscrimination Sign', data.nameSignSecond || '')}
    ${finalSignaturesHTML}
  `;

  // Fix image paths: convert relative paths to absolute URLs
  const origin = window.location.origin;
  agreementHTML = agreementHTML.replace(/src="\/static\//g, `src="${origin}/static/`);
  agreementHTML = agreementHTML.replace(/src="\/([^"]*?)"/g, (match, path) => {
    if (path.startsWith('http') || path.startsWith('data:')) return match;
    return `src="${origin}/${path}"`;
  });

  // Copy stylesheets from parent document, EXCLUDING @media print rules
  const styleSheets = Array.from(document.styleSheets);
  let cssText = '';
  styleSheets.forEach((sheet) => {
    try {
      if (sheet.cssRules) {
        Array.from(sheet.cssRules).forEach((rule) => {
          // Skip @media print rules (they contain visibility:hidden that blanks the page)
          if (rule instanceof CSSMediaRule && rule.conditionText && rule.conditionText.includes('print')) {
            return;
          }
          cssText += rule.cssText + '\n';
        });
      }
    } catch (e) {
      // Skip cross-origin stylesheets
      if (sheet.href) {
        cssText += `@import url("${sheet.href}");\n`;
      }
    }
  });

  // Build the full HTML page
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>MOU - Memorandum of Understanding</title>
      <style>
        ${cssText}

        /* Show signature images */
        .signature-print-only {
          display: inline-block !important;
        }

        /* Document header (program title + school info) */
        .mou-doc-header {
          padding: 20px 41pt 10px 41pt;
          font-family: Arial, sans-serif;
        }
        .mou-doc-title {
          text-align: center;
          font-size: 18px;
          font-weight: bold;
          margin: 0 0 20px 0;
        }
        .mou-school-info {
          display: flex;
          justify-content: space-between;
          gap: 40px;
        }
        .mou-school-col {
          flex: 1;
        }
        .mou-school-col p {
          margin: 4px 0;
          font-size: 13px;
        }

        /* Inline acknowledgement blocks between sections (matching page form style) */
        .mou-acknowledgement {
          padding: 30px 41pt 40px 41pt;
          page-break-inside: avoid;
          font-family: Arial, sans-serif;
        }
        .mou-ack-section-label {
          font-size: 13px;
          font-style: italic;
          margin: 0 0 16px 0;
          text-align: left;
        }
        .mou-ack-input-row {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .mou-ack-field-label {
          font-weight: bold;
          font-size: 13px;
          margin: 0 0 2px 0;
        }
        .mou-ack-name-display {
          font-size: 13px;
          font-style: italic;
          color: #333;
          margin: 0 0 4px 0;
        }
        .mou-ack-input {
          width: 100%;
          max-width: 350px;
          border: 1px solid #ddd;
          border-bottom: 2px solid #ccc;
          background: #f7f7f7;
          padding: 10px 14px;
          font-size: 14px;
          min-height: 20px;
          border-radius: 0;
          color: #333;
        }
        .mou-ack-agree-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .mou-ack-agree-label {
          font-size: 14px;
          color: #333;
        }

        /* Content wrapper */
        .mou-content-wrapper {
          padding: 20px;
          max-width: 900px;
          margin-left: auto;
          margin-right: auto;
          background: white;
        }

        /* Remove scroll containers for full display */
        .agreement-div.scroll,
        .agreement-div {
          overflow: visible !important;
          max-height: none !important;
          height: auto !important;
          border: none !important;
          box-shadow: none !important;
        }

        body {
          background: white;
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
        }

        /* Clean print styles for this window only */
        @media print {
          body, body * {
            visibility: visible !important;
          }
          .mou-content-wrapper {
            padding: 0 !important;
            max-width: 100% !important;
            margin: 0 !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .signature-print-only {
            display: inline-block !important;
          }
          @page {
            margin: 0.5in;
            size: letter;
          }
        }
      </style>
    </head>
    <body>
      <div class="mou-content-wrapper">
        ${headerHTML}
        ${agreementHTML}
      </div>
      <script>
        // Auto-trigger print after content and images load
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 500);
        };
      </script>
    </body>
    </html>
  `;

  // Open new window and write content
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};

export default downloadMOU;
